import {
  ShieldCheck,
  CalendarRange,
  ListChecks,
  Lock,
  MessagesSquare,
  SlidersHorizontal,
  Briefcase,
  Layers,
  Globe,
  Languages,
  Headset,
  FileText,
  Zap,
  Monitor,
  GraduationCap,
} from "lucide-react";

export const BOOKING_URL = process.env.REACT_APP_BOOKING_URL || "";

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "About", to: "/about" },
  { label: "Packages", to: "/packages" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
];

export const IMAGES = {
  hero: "https://images.unsplash.com/photo-1487017159836-4e23ece2e4cf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBleGVjdXRpdmUlMjBvZmZpY2UlMjBtaW5pbWFsJTIwd29ya3NwYWNlJTIwZGVzayUyMGxhcHRvcHxlbnwwfHx8fDE3ODcxNTM5MTR8MA&ixlib=rb-4.1.0&q=85",
  moody:
    "https://images.pexels.com/photos/34549315/pexels-photo-34549315.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  bright:
    "https://images.pexels.com/photos/7583951/pexels-photo-7583951.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  meeting:
    "https://images.unsplash.com/photo-1577415124269-fc1140a69e91?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHw0fHxleGVjdXRpdmUlMjBidXNpbmVzcyUyMGxlYWRlciUyMGNvbnN1bHRpbmclMjBvZmZpY2UlMjBwb3J0cmFpdCUyMG1lZXRpbmd8ZW58MHx8fHwxNzg3MTUzOTIzfDA&ixlib=rb-4.1.0&q=85",
  board:
    "https://images.unsplash.com/photo-1714974528693-f77f6fcc56af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxleGVjdXRpdmUlMjBidXNpbmVzcyUyMGxlYWRlciUyMGNvbnN1bHRpbmclMjBvZmZpY2UlMjBwb3J0cmFpdCUyMG1lZXRpbmd8ZW58MHx8fHwxNzg3MTUzOTIzfDA&ixlib=rb-4.1.0&q=85",
  skyline:
    "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
};

export const SERVICES = [
  {
    slug: "business-executive-support",
    icon: Briefcase,
    title: "Virtual Business & Executive Support",
    tagline:
      "Professional remote administrative and executive support for entrepreneurs, executives, professionals, and businesses worldwide.",
    price: "From $30 USD/hour",
    featured: [
      "Email & Inbox Management",
      "Calendar & Scheduling",
      "Executive Assistance",
      "Meeting Coordination",
      "Project Administration",
    ],
    moreLabel: "See All Business Support Services",
    groups: [
      {
        heading: null,
        items: [
          "Email and inbox management",
          "Calendar management",
          "Appointment scheduling",
          "Meeting scheduling",
          "Meeting coordination",
          "Executive calendar management",
          "General virtual assistant support",
          "Administrative assistance",
          "Data entry",
          "File organization",
          "Digital filing",
          "Database updates",
          "Online research",
          "Administrative follow-ups",
          "Task tracking",
          "Spreadsheet maintenance",
          "Agenda preparation",
          "Meeting minutes",
          "Meeting notes",
          "Action-item tracking",
          "Professional correspondence",
          "Travel research",
          "Travel coordination",
          "Event coordination",
          "Project administration",
          "Stakeholder coordination",
          "Business organization",
          "Confidential administrative support",
          "Presentation preparation",
          "Internal trackers",
          "Reporting support",
          "Temporary administrative coverage",
          "Ongoing monthly administrative support",
        ],
      },
    ],
    pricing: [
      { label: "Regular Support", value: "From $30/hour" },
      { label: "Priority Support", value: "From $45/hour" },
      { label: "Monthly Packages", value: "Available by quote" },
    ],
    cta: "Request a Quote",
  },
  {
    slug: "translation-interpretation",
    icon: Languages,
    title: "Translation & Interpretation",
    tagline:
      "General and business translation and interpretation for business communication, meetings, documents, websites, presentations, customer interactions, and other non-regulated purposes.",
    price: "From $0.12 USD/word",
    priceNote: "Interpretation from $40 USD/hour",
    meta: "English | Українська | العربية | Other Languages",
    featured: [
      "Business Translation",
      "Document Translation",
      "Video Interpretation",
      "Telephone Interpretation",
      "Website Localization",
    ],
    moreLabel: "See All Language Services",
    groups: [
      {
        heading: "Translation",
        items: [
          "English ↔ Ukrainian",
          "Other language pairs on request",
          "English ↔ Arabic",
          "Business document translation",
          "Email translation",
          "Website translation",
          "Presentation translation",
          "Marketing material translation",
          "Product-description translation",
          "Customer communication translation",
          "Forms and questionnaires",
          "Policies and procedures",
          "General correspondence",
          "Proofreading translated documents",
          "Translation editing",
          "Localization",
          "Machine-translation review",
          "Urgent business translation",
          "After-hours translation",
        ],
      },
      {
        heading: "Interpretation",
        items: [
          "Remote interpretation",
          "Telephone interpretation",
          "Video interpretation",
          "Business meeting interpretation",
          "Appointment interpretation",
          "Customer-service interpretation",
          "Community interpretation",
          "Scheduled interpretation",
          "Urgent business interpretation",
          "After-hours interpretation",
        ],
      },
    ],
    pricing: [
      { label: "Standard Translation", value: "From $0.12/word" },
      { label: "Custom Business Projects", value: "Request a Quote" },
      { label: "24-Hour Rush Translation", value: "Standard rate + 35%" },
      { label: "Under 6 Hours", value: "Standard rate + 50%" },
      { label: "Urgent Business Translation", value: "Custom Quote" },
      { label: "Scheduled Business Interpretation", value: "From $40/hour" },
      { label: "Priority Business Interpretation", value: "From $55/hour" },
      { label: "After-Hours Business Interpretation", value: "From $65/hour" },
    ],
    note: "VCK Business Lab does not represent its standard translation or interpretation services as certified, sworn, court-certified, legal, or officially accredited unless expressly stated for a specific project and delivered by an appropriately qualified professional.",
    cta: "Request a Language Services Quote",
  },
  {
    slug: "multilingual-client-support",
    icon: Headset,
    title: "Multilingual Client & Customer Support",
    tagline:
      "Multilingual customer and administrative support that helps businesses communicate professionally with clients across languages and time zones.",
    price: "From $35 USD/hour",
    meta: "English | Українська | العربية | Other Languages",
    featured: [
      "Multilingual Customer Support",
      "Client Follow-Up",
      "Appointment Booking",
      "Email Communication",
      "International Client Coordination",
    ],
    moreLabel: "See All Multilingual Services",
    groups: [
      {
        heading: null,
        items: [
          "Multilingual email correspondence",
          "Customer email support",
          "Client communication",
          "Client follow-up",
          "Appointment scheduling",
          "Appointment reminders",
          "Customer information updates",
          "Client onboarding",
          "Multilingual administrative support",
          "Cross-cultural business communication",
          "International client coordination",
          "Live-chat support",
          "Customer-service support",
          "FAQ support",
          "CRM updates",
          "Database maintenance",
          "Service coordination",
          "Order follow-up",
          "Complaint and issue intake",
          "Escalation coordination",
          "Multilingual business correspondence",
          "Multilingual document preparation",
          "International time-zone coordination",
        ],
      },
    ],
    pricing: [
      { label: "Standard Multilingual Support", value: "From $35/hour" },
      { label: "Priority Support", value: "From $50/hour" },
      { label: "Ongoing Monthly Support", value: "Custom Package" },
    ],
    cta: "Build a Custom Support Package",
  },
  {
    slug: "documents-office-project",
    icon: FileText,
    title: "Documents, Office & Project Services",
    tagline:
      "Professional document preparation, formatting, organization, research, and project support for businesses and professionals.",
    price: "From $30 USD/hour",
    featured: [
      "Word & PDF Formatting",
      "Excel & Trackers",
      "Presentations",
      "Business Documents",
      "Research & Project Support",
    ],
    moreLabel: "See All Document Services",
    groups: [
      {
        heading: "Documents",
        items: [
          "Microsoft Word formatting",
          "PDF preparation",
          "PDF formatting",
          "Forms",
          "Fillable documents",
          "Business templates",
          "Professional letters",
          "Reports",
          "Meeting packages",
          "Resume formatting",
          "Cover-letter formatting",
          "Proofreading",
          "Document cleanup",
          "Document redesign",
          "File conversion",
          "Digital filing",
          "Document organization",
        ],
      },
      {
        heading: "Spreadsheets",
        items: [
          "Excel spreadsheets",
          "Trackers",
          "Data organization",
          "Expense trackers",
          "Invoice trackers",
          "Budget trackers",
          "Contact lists",
          "Project trackers",
          "Deadline trackers",
        ],
      },
      {
        heading: "Presentations & Design",
        items: [
          "PowerPoint presentations",
          "Presentation formatting",
          "Canva business materials",
          "Business flyers",
          "Basic brochures",
          "Social-media graphics",
          "Professional templates",
        ],
      },
      {
        heading: "Project & Research Support",
        items: [
          "Market research",
          "Competitor research",
          "Vendor research",
          "Product research",
          "Online research",
          "Data gathering",
          "Comparison spreadsheets",
          "Contact-list building",
          "Project administration",
          "Deadline management",
          "Follow-up coordination",
          "Research summaries",
        ],
      },
    ],
    pricing: [
      { label: "Hourly Projects", value: "From $30/hour" },
      { label: "Fixed-Price Documents", value: "Available by quote" },
      { label: "Large Projects", value: "Custom Quote" },
    ],
    cta: "Request a Project Quote",
  },
  {
    slug: "website-building",
    icon: Monitor,
    title: "Website Building & Support",
    tagline:
      "Professional websites for modern businesses — we can simply design and build it for you, or keep it updated and supported long-term through a monthly support plan.",
    price: "From $40 USD/hour",
    priceNote: "One-time builds quoted per project",
    featured: [
      "Business Websites",
      "Landing Pages",
      "Website Redesign",
      "Online Store Setup",
      "Ongoing Maintenance",
    ],
    moreLabel: "See All Website Services",
    groups: [
      {
        heading: "Design & Build — One-Time Projects",
        items: [
          "Business website design and build",
          "Landing page creation",
          "Website redesign and refresh",
          "Mobile-friendly responsive layouts",
          "Online store / e-commerce setup",
          "Contact and booking form setup",
          "Multilingual website setup",
          "Portfolio and gallery pages",
          "Blog and content sections",
          "Menu, price-list, and catalog pages",
        ],
      },
      {
        heading: "Ongoing Support — With Any Monthly Plan",
        items: [
          "Content updates and changes",
          "Plugin, theme, and platform updates",
          "Regular website maintenance",
          "Backup and security checks",
          "Domain and hosting coordination",
          "Basic on-page SEO setup",
          "Analytics and performance checks",
          "Website troubleshooting and fixes",
        ],
      },
    ],
    pricing: [
      { label: "Website Updates & Support", value: "From $40/hour" },
      { label: "One-Time Website Build", value: "Request a Quote" },
      { label: "Ongoing Care with a Monthly Plan", value: "See Packages" },
    ],
    note: "Have us simply build your website as a one-time project — or pair it with any Ongoing Support Plan and we will keep it updated, secure, and growing with your business month after month.",
    cta: "Discuss Your Website",
  },
  {
    slug: "newcomer-student-support",
    icon: GraduationCap,
    title: "Newcomer & International Student Support",
    tagline: "Post-arrival administrative & language support in Canada.",
    price: "From $30 USD/hour",
    meta: "English | Українська | العربية | Other Languages",
    featured: [
      "Appointment Booking & Scheduling",
      "Telephone & Email Assistance",
      "General Translation & Interpretation",
      "Practical Post-Arrival Support",
    ],
    moreLabel: "View details",
    groups: [
      {
        heading: "What this covers",
        items: [
          "Practical multilingual administrative and language support for newcomers, international students, and individuals with limited English proficiency navigating everyday life in Canada.",
        ],
      },
      {
        heading: "Everyday coordination",
        items: [
          "Appointment booking and scheduling",
          "Telephone and email assistance",
          "Communication with Canadian businesses and service providers",
          "Follow-ups and general coordination",
        ],
      },
      {
        heading: "Language & documents",
        items: [
          "Help understanding general correspondence and everyday documents",
          "General translation and interpretation",
          "Multilingual support for everyday administrative matters",
        ],
      },
      {
        heading: "Settling in",
        items: [
          "Assistance communicating with schools, colleges, employers, and other organizations",
          "General administrative assistance",
          "Help locating publicly available services and resources",
          "Practical post-arrival support",
        ],
      },
    ],
    pricing: [
      { label: "Hourly Support", value: "From $30/hour" },
      { label: "Priority Requests", value: "From $45/hour" },
      { label: "Ongoing Support", value: "Custom quote" },
    ],
    note: "Important: VCK Business Lab provides general administrative, language, and practical support only. We do not provide immigration, citizenship, legal, tax, financial, medical, employment, educational, or other regulated professional advice or representation. Where regulated professional assistance is required, clients should consult an appropriately licensed professional.",
    cta: "Request a Quote",
  },
  {
    slug: "priority-emergency-support",
    icon: Zap,
    title: "24/7 Priority & Emergency Support",
    tagline:
      "Urgent multilingual, administrative, document, interpretation, and business support for clients who cannot wait until regular business hours.",
    price: "From $65 USD/hour",
    meta: "Available worldwide, subject to team availability.",
    premium: true,
    featured: [
      "Urgent Business Translation",
      "Urgent Business Interpretation",
      "Last-Minute Documents",
      "After-Hours Business Support",
      "Weekend & Holiday Support",
    ],
    moreLabel: "See Emergency Services",
    groups: [
      {
        heading: null,
        items: [
          "Urgent translation",
          "Urgent business interpretation",
          "Last-minute document preparation",
          "Emergency document formatting",
          "Urgent presentations",
          "After-hours administrative support",
          "Weekend administrative support",
          "Holiday support",
          "International time-zone support",
          "Urgent client communication",
          "Urgent multilingual communication",
          "Emergency meeting coordination",
          "Last-minute scheduling",
          "Urgent business research",
          "Emergency customer-service support",
        ],
      },
    ],
    pricing: [
      { label: "Priority Support", value: "From $45/hour" },
      { label: "After-Hours Support", value: "From $55/hour" },
      { label: "24/7 Emergency Support", value: "From $65/hour" },
      { label: "Critical / Under 2-Hour Response", value: "From $75/hour" },
      { label: "Major Holiday Emergency Support", value: "From $85/hour" },
      { label: "Minimum Emergency Service Charge", value: "$65 USD" },
    ],
    note: "Emergency and after-hours services are subject to availability. Response time is confirmed before the client is charged.",
    cta: "Request Emergency Support",
  },
];

export const TOOLS = [
  "Microsoft Word",
  "Excel",
  "Outlook",
  "Teams",
  "PowerPoint",
  "PDF / Acrobat",
  "Google Workspace",
  "Zoom",
];

export const STEPS = [
  {
    num: "01",
    title: "Connect",
    text: "Schedule an introductory consultation and discuss your business needs.",
  },
  {
    num: "02",
    title: "Customize",
    text: "Choose the type and level of support that works best for you.",
  },
  {
    num: "03",
    title: "Delegate",
    text: "Send the administrative tasks you want taken off your plate.",
  },
  {
    num: "04",
    title: "Focus",
    text: "Spend more time on your clients, projects, and business growth while VCK handles the administrative details.",
  },
];

export const WHY_POINTS = [
  { icon: ShieldCheck, title: "Professional & Dependable", text: "Consistent, reliable service you can build your schedule around." },
  { icon: CalendarRange, title: "Flexible Virtual Support", text: "Support that scales up or down with your workload — no rigid contracts." },
  { icon: ListChecks, title: "Detail-Oriented Approach", text: "Every task is handled carefully, checked, and delivered to a high standard." },
  { icon: Lock, title: "Confidential by Default", text: "Business information is handled with strict confidentiality and reasonable safeguards; confidentiality agreements are available when needed." },
  { icon: MessagesSquare, title: "Clear Communication", text: "Professional, prompt, and transparent communication at every step." },
  { icon: SlidersHorizontal, title: "Customized to You", text: "Support shaped around each client's tools, preferences, and way of working." },
  { icon: Briefcase, title: "No Full-Time Hire Needed", text: "Senior-level support without the cost and commitment of an employee." },
  { icon: Layers, title: "Ongoing or Project-Based", text: "Available for continuing monthly support or one-time assignments." },
  { icon: Globe, title: "Worldwide Virtual Service", text: "Serving clients across Canada, the US, Europe, and internationally." },
];

export const CLIENT_TYPES = [
  "Entrepreneurs",
  "Small Businesses",
  "Consultants",
  "Executives",
  "Professionals",
  "Start-ups",
  "Independent Contractors",
  "Busy Business Owners",
  "Organizations Needing Temporary Administrative Support",
  "Individuals",
  "Newcomers to Canada",
  "International Students",
  "International Families",
  "Professionals Relocating to Canada",
  "Individuals with Limited English Proficiency",
  "Ukrainian-Speaking Clients",
  "Arabic-Speaking Clients",
];

export const PACKAGES = [
  {
    slug: "essential",
    name: "Essential",
    price: "From $300 USD/month",
    fit: "Best for entrepreneurs and professionals needing occasional virtual support.",
    popular: false,
    features: [
      "Up to 10 support hours",
      "Additional hours available at add-on rates",
      "Administrative support",
      "Scheduling",
      "Documents",
      "Email support",
    ],
    cta: "Request Essential Package",
  },
  {
    slug: "professional",
    name: "Professional",
    price: "From $575 USD/month",
    fit: "Best for businesses needing consistent weekly support.",
    popular: true,
    features: [
      "Up to 20 support hours",
      "Additional hours available at add-on rates",
      "Administrative support",
      "Multilingual support",
      "Meeting coordination",
      "Project tracking",
      "Priority scheduling",
    ],
    cta: "Request Professional Package",
  },
  {
    slug: "executive",
    name: "Executive",
    price: "From $1,100 USD/month",
    fit: "For executives and businesses requiring ongoing higher-level support.",
    popular: false,
    features: [
      "Up to 60 support hours",
      "Additional hours available at add-on rates",
      "Executive administration",
      "Multilingual client support",
      "Meeting and project coordination",
      "Priority response",
      "Dedicated support structure",
    ],
    cta: "Request Executive Package",
  },
  {
    slug: "global-priority",
    name: "Global Priority",
    price: "Custom Pricing",
    fit: "For companies requiring international, multilingual, extended-hour, or higher-volume support.",
    popular: false,
    features: [
      "Customized monthly hours",
      "Additional hours available at add-on rates",
      "English, Ukrainian, Arabic and other languages support",
      "Priority assistance",
      "Extended-hour availability",
      "Discounted emergency support",
      "Custom workflows",
      "Multiple service categories",
      "Dedicated business support",
    ],
    cta: "Request Custom Quote",
  },
];

export const FAQS = [
  {
    q: "What is a virtual administrative assistant?",
    a: "A virtual administrative assistant is a professional who provides administrative, organizational, and business support remotely. You receive the same dependable, high-quality assistance you would expect from an in-office administrator — without the overhead, equipment, or commitment of a full-time hire.",
  },
  {
    q: "Can I hire VCK Business Lab for only a few hours?",
    a: "Yes. Support is flexible by design. You can engage VCK for just a few hours of occasional help, a single project, or a short-term assignment — there is no minimum monthly commitment required.",
  },
  {
    q: "Do you offer ongoing monthly support?",
    a: "Yes. Our Professional and Executive support packages provide regular weekly and monthly assistance for clients who want consistent, dependable help with their day-to-day administration.",
  },
  {
    q: "Can you work with international clients?",
    a: "Absolutely. All services are delivered 100% virtually, which allows VCK Business Lab to support clients across Canada, the United States, Europe, and internationally. Inquiries from international clients are always welcome.",
  },
  {
    q: "Do you offer one-time projects?",
    a: "Yes. Custom Projects cover one-time assignments such as document formatting, research, event coordination, and workflow organization, each with a clear, fixed-scope quote before work begins.",
  },
  {
    q: "How do I send you documents and information securely?",
    a: "Documents are exchanged through secure, permission-based channels such as encrypted cloud storage and file-sharing platforms. Access is limited strictly to what is needed for your engagement, and confidentiality agreements are always available on request.",
  },
  {
    q: "What software do you work with?",
    a: "VCK works fluently with Microsoft 365 — including Word, Excel, Outlook, Teams, and PowerPoint — as well as Google Workspace, PDF and Acrobat tools, Zoom, and most common business platforms. If your business uses a specific tool, just ask.",
  },
  {
    q: "Can you help with Microsoft Word, Excel, Outlook, Teams, and PDF documents?",
    a: "Yes — these are core to our work. This includes professional Word document formatting, Excel spreadsheets and trackers, Outlook inbox and calendar management, Teams meeting coordination, and PDF preparation and formatting.",
  },
  {
    q: "Are my business documents kept confidential?",
    a: "Always. Confidential handling of business information is a founding principle of VCK Business Lab. Your documents are never shared, discussed, or used beyond the scope of your engagement, and we are glad to formalize this with a non-disclosure agreement.",
  },
  {
    q: "How does billing work?",
    a: "Pricing is transparent and tailored — every engagement uses \u201cstarting from\u201d rates that are finalized after a short consultation. Ongoing packages are invoiced monthly, one-time projects are quoted at a fixed scope, and no long-term contracts are required.",
  },
  {
    q: "How do I pay for services?",
    a: "Once your service requirements, scope, and pricing are confirmed, VCK Business Lab will send you a secure payment link directly. Payment instructions are provided before work begins or according to the payment terms agreed for your service — this website does not offer public checkout.",
  },
  {
    q: "Do you work with individuals and newcomers, or only businesses?",
    a: "Both. Alongside our business services, VCK offers Personal & Newcomer Support — language assistance, help communicating with banks, insurers, utilities, landlords, and schools, and everyday administrative support for individuals, families, and newcomers to Canada.",
  },
  {
    q: "Can you help with immigration applications, legal matters, or financial advice?",
    a: "No — and we are careful to be clear about this. VCK Business Lab does not provide immigration, legal, financial, insurance, tax, or medical advice, and we do not prepare or submit immigration applications. Our personal support services cover communication, organization, and administration only. When a licensed or regulated professional is required, we will always tell you and help you find one.",
  },
];

export const MARQUEE_ITEMS = [
  "Virtual Administrative Support",
  "Executive Calendar Management",
  "Document & Business Support",
  "Meeting Coordination",
  "Business & Project Coordination",
  "Confidential Executive Assistance",
];

export const TESTIMONIALS = [
  {
    quote:
      "Working with this team was a smooth and professional experience from start to finish. They created a modern, clear, and user-friendly website for my legal practice in Ukraine that presents my services in a professional way and makes it easy for clients to find the information they need.\n\nThey listened carefully to my requirements, communicated clearly throughout the process, and delivered a website that reflects my work and professional image very well.\n\nI am very satisfied with the result and would gladly recommend their services to other professionals and businesses looking for reliable website and digital support.",
    name: "Anton",
    detail: "Lawyer, Ukraine",
  },
];

export const LOYALTY_TIERS = [
  {
    months: "1 Month",
    hours: "3 Complimentary Hours",
    note: "A great way to get started and experience VCK support with a little extra on us.",
    extras: [],
    premium: false,
  },
  {
    months: "3 Months",
    hours: "8 Complimentary Hours",
    note: null,
    extras: [
      "Complimentary VCK Welcome Package",
      "Priority scheduling",
      "Flexible support across eligible services",
    ],
    premium: false,
  },
  {
    months: "6 Months",
    hours: "18 Complimentary Hours",
    note: null,
    extras: [
      "Premium VCK Welcome Package",
      "Priority scheduling",
      "Priority project booking",
      "Preferred rates on selected urgent services",
    ],
    premium: false,
  },
  {
    months: "12 Months",
    hours: "36 Complimentary Hours",
    note: null,
    extras: [
      "Premium VCK Welcome Package",
      "Priority scheduling",
      "Priority project booking",
      "Preferred emergency support rates",
      "Complimentary annual support planning session",
      "Exclusive long-term client benefits",
    ],
    premium: true,
  },
];

export const LOYALTY_SERVICES = [
  "Virtual administrative support",
  "Executive support",
  "Multilingual client support",
  "Document and project assistance",
  "Translation services",
  "Other eligible VCK business support services",
];

export const COMPARISON = {
  columns: ["Essential", "Professional", "Executive", "Global Priority"],
  rows: [
    { label: "Monthly support hours", values: ["Up to 10", "Up to 20", "Up to 60", "Customized"] },
    { label: "Administrative support", values: [true, true, true, true] },
    { label: "Multilingual support", values: [false, true, true, true] },
    { label: "Meeting & project coordination", values: [false, true, true, true] },
    { label: "Executive administration", values: [false, false, true, true] },
    { label: "Priority response & scheduling", values: [false, true, true, true] },
    { label: "Extended-hour availability", values: [false, false, false, true] },
    { label: "Discounted emergency support", values: [false, false, false, true] },
    { label: "Dedicated support structure", values: [false, false, true, true] },
    { label: "Price", values: ["From $300/mo", "From $575/mo", "From $1,100/mo", "Custom"] },
  ],
};
